import { css } from '@emotion/react';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Button, Text, useModalControl } from '@frontend/design-system';
import { useCheckCanSendMessages } from '../../../hooks/use-check-can-send-messages';
import { ScheduleRequestActionCalledFrom } from '../../../hooks/use-schedule-request-actions';

type Props = {
  locationId: string;
  patientContactedAt?: string;
  dismissRequestModalProps: ReturnType<typeof useModalControl>;
  onDismissRequestIntent: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onMessage: (updateStatus: boolean, actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onPhoneCall: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onCompleteRequest: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
};

export const Footer = ({
  locationId,
  patientContactedAt,
  onMessage,
  onPhoneCall,
  onCompleteRequest,
  onDismissRequestIntent,
  dismissRequestModalProps,
}: Props) => {
  const { t } = useTranslation('schedule-request');

  const isPatientContacted = !!patientContactedAt;
  const lastContactedOn = isPatientContacted ? formatDate(patientContactedAt, 'MMM D, YYYY, h:mm A') : null;
  const canSendMessages = useCheckCanSendMessages(locationId);

  return (
    <div css={footerContainerStyles}>
      {isPatientContacted && (
        <div>
          <Text size='small' color='subdued'>
            {t('Contacted on {{lastContactedOn}}', { lastContactedOn })}
          </Text>
        </div>
      )}
      <div css={footerButtonStyles}>
        <div css={contactButtonStyles}>
          <Button
            iconName='message'
            variant='secondary'
            disabled={!canSendMessages}
            hoverLabel={canSendMessages ? undefined : t('You do not have permission to send text messages')}
            onClick={() => onMessage(true, 'request_details')}
          >
            {t('Message')}
          </Button>
          <Button iconName='phone' variant='secondary' onClick={() => onPhoneCall('request_details')}>
            {t('Call')}
          </Button>
        </div>
        {isPatientContacted ? (
          <Button variant='tertiary' onClick={() => onCompleteRequest('request_details')}>
            {t('Done')}
          </Button>
        ) : (
          <Button
            variant='tertiary'
            onClick={() => {
              onDismissRequestIntent('request_details');
              dismissRequestModalProps?.openModal();
            }}
          >
            {t('Dismiss')}
          </Button>
        )}
      </div>
    </div>
  );
};

const footerContainerStyles = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xxs);
  margin-top: var(--fabric-space-gap-xxs);
`;

const footerButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--fabric-space-gap-xl);
`;

const contactButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--fabric-space-gap-sm);
  flex: 1;

  & > button {
    color: var(--fabric-color-neutral-70);
    flex: 1;
  }
`;
